import React, { useState, useEffect } from "react";
import { AnimatePresence } from 'framer-motion';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { Zap, Rocket, User, Mail, LayoutGrid, Github, Linkedin } from 'lucide-react';
import DesktopLayout from "./components/DesktopLayout";
import DesktopIcon from "./components/DesktopIcon";
import WindowCard from "./components/WindowCard";
import AlterEgoSection from "./components/AlterEgoSection";
import Portfolio from "./components/Portfolio";
import Projects from "./components/Projects";
import About from "./components/About";
import Skills from "./components/Skills";

const App = () => {
  return (
    <ThemeProvider>
        <AppContent />
    </ThemeProvider>
  );
};

const AppContent = () => {
  const { isAlterEgo } = useTheme();
  const [openWindows, setOpenWindows] = useState([]);
  const [activeWindowId, setActiveWindowId] = useState(null);
  const [zIndexCounter, setZIndexCounter] = useState(1);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const windows = [
    { id: 'home', title: 'Home', type: 'file', component: null, icon: LayoutGrid, color: 'text-blue-500' }, 
    { id: 'projects', title: 'Projects', type: 'folder', component: <Projects />, icon: Rocket, color: 'text-orange-500' },
    { id: 'skills', title: 'Skills', type: 'folder', component: <Skills />, icon: Zap, color: 'text-yellow-500' },
    { id: 'about', title: 'About Me', type: 'file', component: <About />, icon: User, color: 'text-purple-500' },
    { id: 'contact', title: 'Contact', type: 'file', component: <div className="p-6 text-center"><h2 className="text-2xl font-bold mb-4">Get In Touch</h2><p className="mb-4">Feel free to reach out!</p><p className="font-bold">Email: bhimamalbhage1008@gmail.com</p></div>, icon: Mail, color: 'text-red-500' },
    { id: 'github', title: 'GitHub', type: 'link', icon: Github, color: 'text-gray-900', externalUrl: 'https://github.com/bhimamalbhage' },
    { id: 'linkedin', title: 'LinkedIn', type: 'link', icon: Linkedin, color: 'text-blue-700', externalUrl: 'https://www.linkedin.com/in/bmalbhage' },
  ];

  const focusWindow = (id) => {
    const windowToFocus = openWindows.find(w => w.id === id);
    if (windowToFocus && activeWindowId !== id) {
      setOpenWindows(prev => prev.map(w => 
        w.id === id ? { ...w, zIndex: zIndexCounter + 1 } : w
      ));
      setZIndexCounter(prev => prev + 1);
      setActiveWindowId(id);
    }
  };

  const openWindow = (id, title, component) => {
    // If window is already open, just focus it
    if (openWindows.find(w => w.id === id)) {
      focusWindow(id);
      return;
    }
    
    // Replace the current window with the new one (only one window at a time)
    const newWindow = { id, title, component, zIndex: zIndexCounter + 1 };
    setOpenWindows([newWindow]);
    setZIndexCounter(prev => prev + 1);
    setActiveWindowId(id);
  };

  const closeWindow = (id) => {
    setOpenWindows(prev => prev.filter(w => w.id !== id));
  };

  const handleNavigate = (id) => {
    const win = windows.find(w => w.id === id);
    if (win) {
        const component = win.id === 'home' ? <Portfolio onNavigate={handleNavigate} /> : win.component;
        openWindow(win.id, win.title, component);
    }
  };

  // Open Home window on mount
  useEffect(() => {
    openWindow('home', 'Home', <Portfolio onNavigate={handleNavigate} />);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AnimatePresence mode="wait">
        {isAlterEgo ? (
            <AlterEgoSection key="alter-ego" />
        ) : (
            <DesktopLayout 
                key="desktop"
                sidebarIcons={
                    <>
                        {windows.map(win => (
                            <DesktopIcon 
                                key={win.id}
                                label={win.title} 
                                type={win.type}
                                icon={win.icon}
                                color={win.color}
                                onClick={() => {
                                    if (win.externalUrl) {
                                        window.open(win.externalUrl, '_blank');
                                    } else {
                                        const component = win.id === 'home' ? <Portfolio onNavigate={handleNavigate} /> : win.component;
                                        openWindow(win.id, win.title, component);
                                    }
                                }}
                            />
                        ))}
                    </>
                }
                taskbarContent={
                    <div className="flex gap-2">
                        {openWindows.map(win => {
                            const winDef = windows.find(w => w.id === win.id);
                            const Icon = winDef ? winDef.icon : LayoutGrid;
                            return (
                                <button 
                                key={win.id}
                                onClick={() => focusWindow(win.id)}
                                className={`p-2 rounded-xl transition-all relative group ${
                                    activeWindowId === win.id 
                                        ? 'bg-white/20 shadow-inner' 
                                        : 'hover:bg-white/10'
                                }`}
                                title={win.title}
                                >
                                    <Icon className="w-5 h-5 text-gray-700 dark:text-gray-200" />
                                    {/* Active Dot */}
                                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-black/50 dark:bg-white/50 rounded-full"></div>
                                </button>
                            );
                        })}
                    </div>
                }
            >
                {/* Mobile Menu Button */}
                <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="md:hidden fixed top-4 right-4 z-50 p-3 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200"
                >
                    <div className="w-6 h-5 flex flex-col justify-between">
                        <span className={`w-full h-0.5 bg-gray-800 transition-all ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                        <span className={`w-full h-0.5 bg-gray-800 transition-all ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
                        <span className={`w-full h-0.5 bg-gray-800 transition-all ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                    </div>
                </button>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}>
                        <div className="absolute right-0 top-0 h-full w-64 bg-white shadow-2xl p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                            <h2 className="text-xl font-bold mb-6 text-gray-900">Navigation</h2>
                            <div className="space-y-2">
                                {windows.map(win => {
                                    const Icon = win.icon;
                                    return (
                                        <button
                                            key={win.id}
                                            onClick={() => {
                                                if (win.externalUrl) {
                                                    window.open(win.externalUrl, '_blank');
                                                } else {
                                                    const component = win.id === 'home' ? <Portfolio onNavigate={handleNavigate} /> : win.component;
                                                    openWindow(win.id, win.title, component);
                                                }
                                                setMobileMenuOpen(false);
                                            }}
                                            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-left"
                                        >
                                            <Icon className={`w-5 h-5 ${win.color}`} />
                                            <span className="font-medium text-gray-900">{win.title}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Windows */}
                {openWindows.map(win => (
                    <div 
                        key={win.id}
                        className="absolute pointer-events-auto w-full h-full md:w-4/5 md:h-4/5 top-0 md:top-[5%] left-0 md:left-1/2 md:-translate-x-1/2"
                        style={{ 
                            zIndex: win.zIndex,
                        }}
                        onClick={() => focusWindow(win.id)}
                    >
                        <WindowCard 
                            title={win.title} 
                            onClose={() => closeWindow(win.id)}
                            className="h-full w-full"
                        >
                            {win.component}
                        </WindowCard>
                    </div>
                ))}


            </DesktopLayout>
        )}
    </AnimatePresence>
  );
};

export default App;